// redux/slice/saleFormSlice.ts
import { SaletoPublicService } from "@/api/services/transactionService";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";

export interface DetailFormData {
  salesId: string;
  productId: string;
  currencyId: string;
  denomination: string;
  quantity: string;
  rate: string;
  typeId: string;
  vtm: string;
  value: string;
  prefix: string;
  from: string;
  to: string;
  cardNo: string;
}

export interface CommissionFormData {
  salesId: string;
  agentId: string;
  currencyId: string;
  vtm: string;
}

export interface PaymentFormData {
  salesId: string;
  paymentId: string;
  account: string;
  amount: number;
  chequeNumber: string;
  bankName: string;
  chequedate: string;
  inclusiveGst: boolean;
  inclusiveGstValue: string;
  group: string;
  id: string;
}
export interface Encashment {
  id: string;
  saleId: string;
  currency: string;
  currencyName: string;
  buyer: string;
  certificateDate: string; // ISO Date string
  certificateNumber: string;
  fileName: string;
  remark: string;
  fileType: string;
  fileSize: string;
  created: string; // ISO Date string
  status: number;
}

export interface UploadFormData {
  id: string;
  salesId: string;
  remarks: string;
  fileName?: string;
  inclGST?: boolean;
  inclGSTValue?: string;
  file: any;
}

export interface InfoFormData {
  id: string;
  salesId: string;
  location: string;
  date: string;
  saleTypeId: string;
  manualNo: string;
  executiveId: string;
  modeId: string;
  cseNameId: string;
  scheduleTime: string;
  travellerId: string;
  identityId: string;
  identityName: string;
  purpose: string;
  residentialStatusId: string;
  travellerEmployeeCode: string;
  corporate: string;
  placeOfSupplyId: string;
  pan_Form60: string;
  sourceOfFund: string;
  countryId: string;
  ticketClassId: string;
  flightNo: string;
  ticketNo: string;
  depDate: string;
  visaTypeId: string;
  duration: string;
  insurance: boolean;
  flagId: string;
  transactionTypeId: string;
  status: string;
  saleStatus: string;
  dealerId?: string;
  dealerType: string;
  saleEncashmentCertificate: Encashment;
  salesDetails: DetailFormData[];
  saleToPubDetails?: DetailFormData[];
  salesCommission: CommissionFormData[];
  saleToPubCommissions?: CommissionFormData[];
  salesPayment: PaymentFormData[];
  saleToPubPayment?: PaymentFormData[];
  salesDocument: UploadFormData[];
  saleToPubDocuments?: UploadFormData[];
}

interface SaleFormState {
  info: InfoFormData;
  editInfo: InfoFormData;
  error: string | undefined;
}
const initialInfo: InfoFormData = {
  id: "",
  salesId: "",
  location: "",
  date: dayjs().toISOString(),
  saleTypeId: "",
  manualNo: "",
  executiveId: "",
  modeId: "",
  cseNameId: "",
  scheduleTime: "",
  travellerId: "",
  identityId: "",
  identityName: "",
  purpose: "",
  residentialStatusId: "",
  travellerEmployeeCode: "",
  corporate: "",
  placeOfSupplyId: "",
  pan_Form60: "",
  sourceOfFund: "",
  countryId: "",
  ticketClassId: "",
  flightNo: "",
  ticketNo: "",
  dealerId: "",
  dealerType: "",
  depDate: dayjs().toISOString(),
  visaTypeId: "",
  duration: "",
  insurance: false,
  flagId: "",
  transactionTypeId: "",
  status: "",
  saleStatus: "",
  saleEncashmentCertificate: {
    id: "",
    saleId: "",
    currency: "",
    currencyName: "",
    buyer: "",
    certificateDate: "", // ISO Date string
    certificateNumber: "",
    fileName: "",
    remark: "",
    fileType: "",
    fileSize: "",
    created: "", // ISO Date string
    status: 0,
  },
  salesDetails: [],
  salesCommission: [],
  salesPayment: [],
  salesDocument: [],
};

const initialState: SaleFormState = {
  info: { ...initialInfo },
  editInfo: { ...initialInfo },
  error: "",
};

export const fetchSaleInfo = createAsyncThunk<
  InfoFormData,
  string,
  { rejectValue: string }
>("saleForm/fetchSaleInfo", async (taskId, { rejectWithValue }) => {
  try {
    const response = await SaletoPublicService.getbyId(taskId);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch sale info");
  }
});

const saleFormSlice = createSlice({
  name: "saleForm",
  initialState,
  reducers: {
    setInfoData(state, action: PayloadAction<InfoFormData>) {
      state.info = action.payload;
    },
    setDetailData(state, action: PayloadAction<DetailFormData[]>) {
      state.info.salesDetails = action.payload;
    },
    setCommissionData(state, action: PayloadAction<CommissionFormData[]>) {
      state.info.salesCommission = action.payload;
    },
    setPaymentData(state, action: PayloadAction<PaymentFormData[]>) {
      state.info.salesPayment = action.payload;
    },
    setUploadData(state, action: PayloadAction<UploadFormData[]>) {
      state.info.salesDocument = action.payload;
    },
    setEncashmentData(state, action: PayloadAction<Encashment>) {
      state.info.saleEncashmentCertificate = action.payload;
    },
    resetSaleForm(state) {
      state.info = { ...initialInfo };
      state.editInfo = { ...initialInfo };
      state.error = "";
    },
    setEditInfoData(state, action: PayloadAction<InfoFormData>) {
      state.editInfo = action.payload;
    },
    setEditCommissionData(state, action: PayloadAction<CommissionFormData[]>) {
      state.editInfo.saleToPubCommissions = action.payload;
    },
    setEditDetailData(state, action: PayloadAction<DetailFormData[]>) {
      state.editInfo.saleToPubDetails = action.payload;
    },
    setEditPaymentData(state, action: PayloadAction<PaymentFormData[]>) {
      state.editInfo.saleToPubPayment = action.payload;
    },
    setEditUploadData(state, action: PayloadAction<UploadFormData[]>) {
      state.editInfo.saleToPubDocuments = action.payload;
    },
    setEditEncashment(state, action: PayloadAction<Encashment>) {
      state.editInfo.saleEncashmentCertificate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSaleInfo.pending, (state) => {
        state.error = undefined;
      })
      .addCase(fetchSaleInfo.fulfilled, (state, action) => {
        state.editInfo = action.payload;
        sessionStorage.setItem("SalesId", action.payload.salesId);
      })
      .addCase(fetchSaleInfo.rejected, (state, action) => {
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Unknown error occurred";
      });
  },
});

export const {
  setInfoData,
  setDetailData,
  setCommissionData,
  setPaymentData,
  setUploadData,
  setEncashmentData,
  resetSaleForm,

  setEditCommissionData,
  setEditDetailData,
  setEditInfoData,
  setEditPaymentData,
  setEditUploadData,
  setEditEncashment,
} = saleFormSlice.actions;

export default saleFormSlice.reducer;
